/**
 * Hook para gerenciar filtros hierárquicos
 * Banco > Instituição > Tipo de Operação > Correspondente
 */
import { useState, useMemo, useCallback } from "react";

export function useFilters({ bancos, instituicoes, tabelasComissao, getNomeInstituicao }) {
  // Estados de seleção
  const [bancoSelecionado, setBancoSelecionado] = useState(null);
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState(null);
  const [tipoOperacaoSelecionado, setTipoOperacaoSelecionado] = useState(null);
  const [correspondenteSelecionado, setCorrespondenteSelecionado] = useState(null);

  // Limpar todos os filtros
  const limparFiltros = useCallback(() => {
    setBancoSelecionado(null);
    setInstituicaoSelecionada(null);
    setTipoOperacaoSelecionado(null);
    setCorrespondenteSelecionado(null);
  }, []);

  // Selecionar banco (limpa filtros dependentes)
  const selecionarBanco = useCallback((bancoId) => {
    if (bancoId === "__all__" || !bancoId) {
      setBancoSelecionado(null);
    } else {
      const banco = bancos.find(b => b.id === bancoId);
      setBancoSelecionado(banco || null);
    }
    setInstituicaoSelecionada(null);
    setTipoOperacaoSelecionado(null);
  }, [bancos]);

  // Selecionar instituição (limpa filtros dependentes)
  const selecionarInstituicao = useCallback((inst) => {
    setInstituicaoSelecionada(inst === "__all__" ? null : inst);
    setTipoOperacaoSelecionado(null);
  }, []);

  // Selecionar tipo de operação
  const selecionarTipoOperacao = useCallback((tipo) => {
    setTipoOperacaoSelecionado(tipo === "__all__" ? null : tipo);
  }, []);

  // Selecionar correspondente
  const selecionarCorrespondente = useCallback((corresp) => {
    setCorrespondenteSelecionado(corresp === "__all__" ? null : corresp);
  }, []);

  // Obter instituições vinculadas ao banco selecionado
  const instituicoesVinculadas = useMemo(() => {
    const codigos = bancoSelecionado?.instituicoes || [];
    return codigos.map(codigo => ({
      codigo: codigo,
      nomeCompleto: getNomeInstituicao(codigo)
    }));
  }, [bancoSelecionado, getNomeInstituicao]);

  // Filtrar tabelas com base nos filtros selecionados
  const tabelasFiltradas = useMemo(() => {
    return tabelasComissao.filter(t => {
      if (bancoSelecionado && t.banco_id !== bancoSelecionado.id) return false;
      
      // Comparação flexível de instituição (código ou nome completo)
      if (instituicaoSelecionada) {
        const instTabela = t.instituicao || "";
        const codigoTabela = instTabela.split(" - ")[0];
        const codigoFiltro = instituicaoSelecionada.split(" - ")[0];
        
        if (codigoTabela !== codigoFiltro && instTabela !== instituicaoSelecionada) {
          return false;
        }
      }
      
      if (tipoOperacaoSelecionado && t.tipo_operacao !== tipoOperacaoSelecionado) return false;
      if (correspondenteSelecionado && t.correspondente !== correspondenteSelecionado) return false;
      return true;
    });
  }, [tabelasComissao, bancoSelecionado, instituicaoSelecionada, tipoOperacaoSelecionado, correspondenteSelecionado]);

  // Agrupar tabelas por tipo de operação
  const tabelasAgrupadasPorTipo = useMemo(() => {
    const grupos = {};
    tabelasFiltradas.forEach(t => {
      const tipo = t.tipo_operacao || "Sem Tipo";
      if (!grupos[tipo]) grupos[tipo] = [];
      grupos[tipo].push(t);
    });
    return grupos;
  }, [tabelasFiltradas]);

  // Agrupar TODAS as tabelas por banco (para consulta)
  const tabelasAgrupadasPorBanco = useMemo(() => {
    const grupos = {};
    tabelasComissao.forEach(t => {
      const bancoId = t.banco_id || "sem_banco";
      if (!grupos[bancoId]) {
        grupos[bancoId] = {
          banco_id: bancoId,
          tabelas: [],
          tipos: new Set(),
          instituicoes: new Set()
        };
      }
      grupos[bancoId].tabelas.push(t);
      if (t.tipo_operacao) grupos[bancoId].tipos.add(t.tipo_operacao);
      if (t.instituicao) grupos[bancoId].instituicoes.add(t.instituicao);
    });
    
    // Converter Sets para arrays
    Object.values(grupos).forEach(g => {
      g.tipos = Array.from(g.tipos);
      g.instituicoes = Array.from(g.instituicoes);
    });
    
    return grupos;
  }, [tabelasComissao]);

  // Bancos que têm tabelas cadastradas
  const bancosComTabelas = useMemo(() => {
    return bancos.filter(b => tabelasAgrupadasPorBanco[b.id]);
  }, [bancos, tabelasAgrupadasPorBanco]);

  // Verificar se há filtros ativos
  const temFiltrosAtivos = bancoSelecionado || instituicaoSelecionada || tipoOperacaoSelecionado || correspondenteSelecionado;

  return {
    // Estados de seleção
    bancoSelecionado,
    instituicaoSelecionada,
    tipoOperacaoSelecionado,
    correspondenteSelecionado,
    // Ações
    selecionarBanco,
    selecionarInstituicao,
    selecionarTipoOperacao,
    selecionarCorrespondente,
    limparFiltros,
    // Dados computados
    instituicoesVinculadas,
    tabelasFiltradas,
    tabelasAgrupadasPorTipo,
    tabelasAgrupadasPorBanco,
    bancosComTabelas,
    temFiltrosAtivos
  };
}
